import DataTable from "react-data-table-component";
import Im from "immutable";
import Moment from "moment";
import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import Octicon from "react-octicon";

import useApi from "storybook-dashboard/utils/fetching";
import { rpName } from "storybook-dashboard/dashboard/project/reportStatus";
import { useModal, Modal } from "storybook-dashboard/components/modal";
import { Status } from "AppSrc/user/projectItem/reportingPackageStatusItem";
import BulkActionsSelection from "./BulkActionsSelection";

const getStatusName = (commit) => {
  let status = commit?.get("status_name") || "Nothing Received";

  if (status == "Nothing Received") {
    const due_date = commit?.get("due_date");
    if (due_date && new Date(due_date) < new Date()) {
      status = "Overdue";
    }
  }

  return status;
};

const Notifications = ({ setNotificationCounts }) => {
  const { setModal, hideModal } = useModal();
  const [data, setData] = useState([]);
  const [allData, setAllData] = useState([]);
  const [pendingActionsData, setPendingActionsData] = useState([]);
  const [uniqueCompanies, setUniqueCompanies] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [requiresAction, setRequiresAction] = useState(true);
  const [filterText, setFilterText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  // Load column visibility from localStorage or use defaults
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem("notificationsColumnVisibility");
    return saved
      ? JSON.parse(saved)
      : {
          dashboard: true,
          project: true,
          reportingPackage: true,
          reportingPeriod: true,
          status: true,
          lastUpdated: true,
          action: true,
        };
  });

  let { data: projects, isLoading: isLoadingProjects } = useApi(`/api/project/`);
  let { data: refs, isLoading: isLoadingRefs } = useApi(`/api/tracker/ref/?light=true`);
  let { data: companies, isLoading: isLoadingCompanies } = useApi(`/api/company/`);
  const _requiresAction = requiresAction ? "true" : "false";
  let {
    data: commits,
    mutate: mutateCommits,
    isLoading: isLoadingCommits,
  } = useApi(`/api/tracker/commit/?requiresAction={_requiresAction}&light=true`, { _requiresAction: "false" });

  const isLoading = isLoadingProjects || isLoadingRefs || isLoadingCommits || isLoadingCompanies;

  useEffect(() => {
    if (
      projects &&
      commits &&
      companies &&
      refs &&
      !isLoadingProjects &&
      !isLoadingCommits &&
      !isLoadingCompanies &&
      !isLoadingRefs
    ) {
      let projectsById = (projects || []).reduce((a, p) => a.set(p.get("uid"), p), Im.Map());
      let refsById = (refs || []).reduce((a, p) => a.set(p.get("uid"), p), Im.Map());
      let companiesById = (companies || []).reduce((a, c) => a.set(c.get("uid"), c), Im.Map());

      // Get all rows for display (affected by requiresAction checkbox)
      const allRows = commits
        .map((commit) => {
          let project = projectsById.get(commit.get("project"));
          let ref = refsById.get(commit.get("ref_id"));
          return {
            project,
            commit,
            ref,
          };
        })
        .filter((r) => {
          if (!r.ref || r.ref?.get("name") === "master") return false;
          if (r.commit?.get("status_name")?.startsWith("Not for")) return false;
          if (requiresAction && r.commit?.get("status_name")?.startsWith("OK")) return false;
          return true;
        });

      // Get pending actions count (always exclude approved reports, regardless of checkbox)
      const pendingActionsRows = commits
        .map((commit) => {
          let project = projectsById.get(commit.get("project"));
          let ref = refsById.get(commit.get("ref_id"));
          return {
            project,
            commit,
            ref,
          };
        })
        .filter((r) => {
          if (!r.ref || r.ref?.get("name") === "master") return false;
          if (r.commit?.get("status_name")?.startsWith("Not for")) return false;
          // Always exclude approved reports for pending actions count
          if (r.commit?.get("status_name")?.startsWith("OK")) return false;
          return true;
        });

      // Helper function to find the top-level parent company
      const findTopLevelCompany = (companyId) => {
        const company = companiesById?.get(companyId);
        if (!company) return null;

        let parentId = company.get("parentid");
        if (!parentId) return company;

        let parent = companiesById?.get(parentId);
        if (!parent) {
          return company;
        } else {
          return findTopLevelCompany(parent.get("uid"));
        }
      };

      // Use the same approach as CompanyTree: get companies with depth <= 1
      const topLevelCompanies = companies
        .filter((company) => company.get("depth") <= 1)
        .sortBy((company) => company.get("name"));

      const topLevelCompaniesForDropdown = topLevelCompanies.toArray();

      // Apply filters for display
      const filteredRows = allRows.filter((r) => {
        // Apply text filter
        const textMatch =
          !filterText ||
          r.project?.get("name").toLowerCase().includes(filterText.toLowerCase()) ||
          r.project?.getIn(["company", "name"]).toLowerCase().includes(filterText.toLowerCase()) ||
          r.ref?.get("name").toLowerCase().includes(filterText.toLowerCase());

        // Apply status filter
        const statusMatch = !statusFilter || getStatusName(r.commit) === statusFilter;

        // Apply company filter (match against top-level parent company)
        const companyMatch =
          !companyFilter ||
          (() => {
            const projectCompanyId = r.project?.getIn(["company", "uid"]);
            const topLevelCompany = findTopLevelCompany(projectCompanyId);
            return topLevelCompany?.get("uid") === companyFilter;
          })();

        return textMatch && statusMatch && companyMatch;
      });

      setAllData(allRows.toArray());
      setData(filteredRows.toArray());
      setPendingActionsData(pendingActionsRows.toArray());
      setUniqueCompanies(topLevelCompaniesForDropdown);

      if (setNotificationCounts) {
        // If company filter is active, show filtered count for that company
        // Otherwise show total pending actions count
        if (companyFilter) {
          // Filter pending actions by the selected company
          const filteredPendingActions = pendingActionsRows.filter((r) => {
            const projectCompanyId = r.project?.getIn(["company", "uid"]);
            const topLevelCompany = findTopLevelCompany(projectCompanyId);
            return topLevelCompany?.get("uid") === companyFilter;
          });
          setNotificationCounts(filteredPendingActions.size);
        } else {
          // Use pendingActionsRows for the total count
          setNotificationCounts(pendingActionsRows.size);
        }
      }
    } else if (setNotificationCounts) {
      // Set count to 0 when data is not available
      setNotificationCounts(0);
    }
  }, [
    projects,
    commits,
    refs,
    companies,
    filterText,
    statusFilter,
    companyFilter,
    requiresAction,
    setNotificationCounts,
  ]);

  const history = useHistory();

  const getActionForStatus = ({ name }) => {
    if (name === null) {
      return "Submit";
    }
    const statusMap = {
      Approved: "",
      "Nothing Received": "Submit",
      "Pending Approval": "Review",
      "Pending Data Control": "Review",
      Overdue: "Submit",
      "Requires Revision": "Revise",
    };
    return statusMap[name];
  };

  const allColumns = [
    {
      id: "dashboard",
      name: <div>Dashboard</div>,
      format: (r) => {
        const companyName = r.project?.getIn(["company", "name"]);
        return (
          <Link
            className="text-primary font-weight-medium text-decoration-none"
            to={`/company/${r.project?.getIn(["company", "uid"])}`}
            style={{ fontSize: "0.875rem" }}
          >
            {companyName}
          </Link>
        );
      },
      selector: (r) => r.project?.getIn(["company", "name"]),
      sortable: true,
      minWidth: "140px",
      grow: 1,
      reorder: true,
      wrap: true,
    },
    {
      id: "project",
      name: <div>Project</div>,
      format: (r) => {
        const projectName = r.project?.get("name");
        return (
          <div>
            <Link
              className="text-dark font-weight-bold text-decoration-none d-block"
              to={`/project/${r.project?.get("uid")}`}
              style={{ fontSize: "0.875rem", lineHeight: "1.4" }}
            >
              {projectName}
            </Link>
          </div>
        );
      },
      selector: (r) => r.project?.get("name"),
      sortable: true,
      minWidth: "140px",
      grow: 2,
      reorder: true,
      wrap: true,
    },
    {
      id: "reportingPackage",
      name: (
        <div style={{ whiteSpace: "normal", lineHeight: "1.2", textAlign: "center" }}>
          Reporting
          <br />
          Package
        </div>
      ),
      format: (r) => {
        const packageName = r.ref?.get("name");
        return (
          <div>
            <Link
              className="text-info font-weight-medium text-decoration-none"
              to={`/project/${r.project?.get("uid")}/wpack/${r.commit?.get("ref_id")}/evals`}
              style={{ fontSize: "0.875rem" }}
            >
              <Octicon name="file" className="mr-1" size="small" />
              {packageName}
            </Link>
          </div>
        );
      },
      selector: (r) => r.ref?.get("name"),
      sortable: true,
      minWidth: "175px",
      grow: 1,
      reorder: true,
      wrap: true,
    },
    {
      id: "reportingPeriod",
      name: (
        <div style={{ whiteSpace: "normal", lineHeight: "1.2", textAlign: "center" }}>
          Reporting
          <br />
          Period
        </div>
      ),
      format: (r) => {
        const periodName = rpName(r.commit);
        return (
          <div className="text-muted" style={{ fontSize: "0.875rem" }}>
            <Octicon name="calendar" className="mr-1" size="small" />
            {periodName}
          </div>
        );
      },
      sortable: true,
      selector: (r) => r.commit?.get("startDate"),
      reorder: true,
      minWidth: "125px",
      wrap: true,
    },
    {
      id: "status",
      name: <div>Status</div>,
      format: (r) => {
        const status = getStatusName(r.commit);

        const statusColorMap = {
          "Pending Approval": "warning",
          "Requires Revision": "info",
          "Nothing Received": "light",
          Overdue: "danger",
          "OK for Submission": "success",
        };

        const statusColor = statusColorMap[status] || "light";

        return (
          <Link
            to={`/project/${r.project?.get("uid")}/wpack/${r.commit?.get("ref_id")}/report/${r.commit?.get("uid")}`}
            className="text-decoration-none d-block"
          >
            <div className={`alert alert-${statusColor} mb-0 py-3 px-4 text-center border-0 rounded`}>
              <span className="font-weight-bold">
                {status == "OK for Submission" ? "Approved" : status || "No status"}
              </span>
            </div>
          </Link>
        );
      },
      sortable: true,
      selector: (r) => getStatusName(r.commit),
      reorder: true,
      center: true,
      minWidth: "150px",
      wrap: true,
    },
    {
      id: "action",
      name: <div>Action</div>,
      format: (r) => {
        const url = `/project/${r.project?.get("uid")}/wpack/${r.commit?.get("ref_id")}/report/${r.commit?.get("uid")}`;
        const statusName = getActionForStatus({ name: r.commit?.get("status_name") });

        if (!statusName) {
          return (
            <div className="text-center">
              <span className="badge badge-light text-muted">No action</span>
            </div>
          );
        }

        const actionIconMap = {
          Submit: "cloud-upload",
          Review: "eye",
          Revise: "pencil",
        };

        return (
          <div className="text-center">
            <button
              type="button"
              className={`btn btn-sm btn-outline-primary`}
              onClick={(e) => {
                e.preventDefault();
                history.push(url);
              }}
              title={`${statusName} this report`}
            >
              <Octicon className="mr-1" name={actionIconMap[statusName] || "arrow-right"} size="small" />
              {statusName}
            </button>
          </div>
        );
      },
      selector: (r) => getActionForStatus({ name: r.commit?.get("status_name") }),
      sortable: true,
      center: true,
      minWidth: "120px",
      wrap: true,
    },
    {
      id: "lastUpdated",
      format: (r) => {
        let lastUpdate = r.commit?.get("meta_json__lastUpdateOn");
        return lastUpdate ? (
          <div className="text-muted" style={{ fontSize: "0.875rem", whiteSpace: "normal", lineHeight: "1.2" }}>
            <Octicon name="clock" className="mr-1" size="small" />
            {Moment(lastUpdate).format("DD MMM")} {Moment(lastUpdate).format("YY")}
          </div>
        ) : (
          <div className="text-muted" style={{ fontSize: "0.875rem", whiteSpace: "normal" }}>
            <Octicon name="dash" className="mr-1" size="small" />
            <br />
            No update
          </div>
        );
      },
      name: (
        <div style={{ whiteSpace: "normal", lineHeight: "1.2", textAlign: "center" }}>
          Last
          <br />
          Updated
        </div>
      ),
      sortable: true,
      selector: (r) => r.commit?.get("meta_json__lastUpdateOn"),
      reorder: true,
      minWidth: "120px",
      wrap: true,
    },
  ];

  // Filter columns based on visibility
  const columns = allColumns.filter((col) => visibleColumns[col.id]);

  const toggleColumnVisibility = (columnId) => {
    const newVisibility = {
      ...visibleColumns,
      [columnId]: !visibleColumns[columnId],
    };
    setVisibleColumns(newVisibility);
    localStorage.setItem("notificationsColumnVisibility", JSON.stringify(newVisibility));
  };

  const setColumnPreset = (preset) => {
    const presets = {
      all: {
        dashboard: true,
        project: true,
        reportingPackage: true,
        reportingPeriod: true,
        status: true,
        lastUpdated: true,
        action: true,
      },
      essential: {
        dashboard: true,
        project: true,
        reportingPackage: false,
        reportingPeriod: false,
        status: true,
        lastUpdated: false,
        action: true,
      },
      compact: {
        dashboard: false,
        project: true,
        reportingPackage: true,
        reportingPeriod: true,
        status: true,
        lastUpdated: false,
        action: true,
      },
    };

    const newVisibility = presets[preset];
    setVisibleColumns(newVisibility);
    localStorage.setItem("notificationsColumnVisibility", JSON.stringify(newVisibility));
  };

  // Calculate visible/total columns for indicator
  const visibleCount = Object.values(visibleColumns).filter(Boolean).length;
  const totalColumns = Object.keys(visibleColumns).length;

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          {/* Filters Card */}
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-light border-bottom">
              <div className="d-flex align-items-center">
                <Octicon name="filter" className="mr-2 text-primary" />
                <h6 className="mb-0 text-dark">Search & Filter</h6>
                {(filterText || statusFilter || companyFilter) && (
                  <span className="badge badge-warning ml-2">Filters Active</span>
                )}
              </div>
            </div>
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-lg-4 col-md-6 mb-3 mb-lg-0">
                  <label className="form-label small text-muted font-weight-bold">SEARCH</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text bg-light border-right-0">
                        <Octicon name="search" className="text-muted" />
                      </span>
                    </div>
                    <input
                      className="form-control border-left-0"
                      type="text"
                      placeholder="Company, project, or package..."
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-lg-3 col-md-6 mb-3 mb-lg-0">
                  <label className="form-label small text-muted font-weight-bold">ACCOUNTS</label>
                  <select
                    className="form-control"
                    value={companyFilter}
                    onChange={(e) => setCompanyFilter(e.target.value)}
                  >
                    <option value="">All Accounts</option>
                    {uniqueCompanies.map((company) => (
                      <option key={company.get("uid")} value={company.get("uid")}>
                        {company.get("name")}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-lg-3 col-md-6 mb-3 mb-lg-0">
                  <label className="form-label small text-muted font-weight-bold">STATUS</label>
                  <select
                    className="form-control"
                    value={statusFilter}
                    onChange={(e) => {
                      const selectedStatus = e.target.value;
                      setStatusFilter(selectedStatus);

                      // Auto-toggle requiresAction based on status selection
                      if (selectedStatus === "OK for Submission") {
                        // Show approved reports when "Approved (no actions)" is selected
                        setRequiresAction(false);
                      } else {
                        // Hide approved reports for all other statuses
                        setRequiresAction(true);
                      }
                    }}
                  >
                    <option value="">All Pending Actions</option>
                    <option value="Nothing Received">Nothing Received</option>
                    <option value="Overdue">Overdue</option>
                    <option value="Pending Approval">Pending Approval</option>
                    <option value="Requires Revision">Requires Revision</option>
                    <option value="OK for Submission">Approved (no actions)</option>
                  </select>
                </div>
                <div className="col-lg-2 col-md-12 text-lg-right">
                  <div className="d-flex justify-content-lg-end align-items-center mb-2">
                    <div className="dropdown">
                      <button
                        className="btn btn-sm btn-link text-muted px-0 border-0"
                        type="button"
                        id="optionsDropdown"
                        data-toggle="dropdown"
                        aria-haspopup="true"
                        aria-expanded="false"
                        // style={{ border: "none", background: "transparent", color: "#6c757d" }}
                      >
                        <Octicon name="gear" className="mr-1" />
                        Options
                        {visibleCount < totalColumns && (
                          <span className="badge badge-warning badge-pill ml-1" style={{ fontSize: "0.65rem" }}>
                            {visibleCount}/{totalColumns}
                          </span>
                        )}
                      </button>
                      <div
                        className="dropdown-menu dropdown-menu-right"
                        aria-labelledby="optionsDropdown"
                        style={{ minWidth: "250px" }}
                      >
                        <h6 className="dropdown-header">Display Options</h6>
                        <div className="dropdown-item-text">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="includeApprovedReports"
                              checked={!requiresAction}
                              onChange={() => setRequiresAction(!requiresAction)}
                            />
                            <label
                              className="form-check-label text-dark"
                              htmlFor="includeApprovedReports"
                              style={{ cursor: "pointer" }}
                            >
                              Include approved reports
                            </label>
                          </div>
                        </div>
                        <div className="dropdown-divider"></div>
                        <h6 className="dropdown-header">Column Visibility</h6>
                        {[
                          { id: "dashboard", label: "Dashboard" },
                          { id: "project", label: "Project" },
                          { id: "reportingPackage", label: "Reporting Package" },
                          { id: "reportingPeriod", label: "Reporting Period" },
                          { id: "status", label: "Status" },
                          { id: "lastUpdated", label: "Last Updated" },
                          { id: "action", label: "Action" },
                        ].map((column) => (
                          <div key={column.id} className="dropdown-item-text">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`column-${column.id}`}
                                checked={visibleColumns[column.id]}
                                onChange={() => toggleColumnVisibility(column.id)}
                              />
                              <label
                                className="form-check-label text-dark"
                                htmlFor={`column-${column.id}`}
                                style={{ cursor: "pointer" }}
                              >
                                {column.label}
                              </label>
                            </div>
                          </div>
                        ))}
                        <div className="dropdown-divider"></div>
                        <h6 className="dropdown-header">Quick Presets</h6>
                        <button
                          className="dropdown-item"
                          onClick={() => setColumnPreset("all")}
                          style={{ cursor: "pointer" }}
                        >
                          <Octicon name="eye" className="mr-2" />
                          Full View <small className="text-muted">(7 columns)</small>
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={() => setColumnPreset("essential")}
                          style={{ cursor: "pointer" }}
                        >
                          <Octicon name="star" className="mr-2" />
                          Essential View <small className="text-muted">(4 columns)</small>
                        </button>
                        <button
                          className="dropdown-item"
                          onClick={() => setColumnPreset("compact")}
                          style={{ cursor: "pointer" }}
                        >
                          <Octicon name="device-mobile" className="mr-2" />
                          Compact View <small className="text-muted">(5 columns)</small>
                        </button>
                      </div>
                    </div>
                  </div>
                  <label className="form-label small text-muted font-weight-bold d-bloc mb-0">RESULTS</label>
                  <div className="d-flex justify-content-lg-end align-items-center">
                    <small className="text-muted">
                      {statusFilter === "OK for Submission" || !requiresAction
                        ? `${data.length} results`
                        : `${data.length} of ${pendingActionsData.length}`}
                    </small>
                    {(filterText || statusFilter || companyFilter) && (
                      <button
                        className="btn btn-sm btn-outline-secondary pr-0 pl-1 py-0 ml-1"
                        onClick={() => {
                          setFilterText("");
                          setStatusFilter("");
                          setCompanyFilter("");
                          setRequiresAction(true);
                        }}
                        title="Clear all filters"
                      >
                        <Octicon name="x" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Data Table Card */}
          <div className="card shadow-sm">
            {selectedRows.length > 0 && (
              <div className="card-header bg-white border-bottom-0">
                <div className="d-flex justify-content-end align-items-center">
                  <span className="badge badge-primary">{selectedRows.length} selected</span>
                </div>
              </div>
            )}
            <div className="card-body p-0">
              <DataTable
                columns={columns}
                data={data}
                highlightOnHover={true}
                onSelectedRowsChange={({ selectedRows }) => setSelectedRows(selectedRows)}
                pagination
                progressPending={isLoading}
                allowOverflow={true}
                noDataComponent={
                  <div className="text-center py-5">
                    <div className="mb-4">
                      <Octicon name="inbox" size="large" className="text-muted" />
                    </div>
                    <h5 className="text-muted mb-3">No pending actions found</h5>
                    <p className="text-muted mb-4">
                      {filterText || statusFilter || companyFilter
                        ? "No results match your current filter criteria."
                        : "All caught up! There are no reports requiring action at this time."}
                    </p>
                    {(filterText || statusFilter || companyFilter) && (
                      <button
                        className="btn btn-outline-primary"
                        onClick={() => {
                          setFilterText("");
                          setStatusFilter("");
                          setCompanyFilter("");
                        }}
                      >
                        <Octicon name="x" className="mr-1" />
                        Clear Filters
                      </button>
                    )}
                  </div>
                }
                customStyles={{
                  rows: {
                    style: {
                      minHeight: "56px",
                      paddingTop: "12px",
                      paddingBottom: "12px",
                      borderBottom: "1px solid #f1f3f4",
                    },
                  },
                  headCells: {
                    style: {
                      backgroundColor: "#f8f9fa",
                      borderBottom: "2px solid #dee2e6",
                      fontWeight: "600",
                      color: "#495057",
                      fontSize: "0.875rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      minHeight: "60px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "8px 12px",
                    },
                  },
                  cells: {
                    style: {
                      fontSize: "0.875rem",
                      color: "#495057",
                      padding: "8px 12px", // Consistent padding for all cells
                    },
                  },
                }}
                paginationComponentOptions={{
                  rowsPerPageText: "Rows per page:",
                  rangeSeparatorText: "of",
                  selectAllRowsItem: true,
                  selectAllRowsItemText: "All",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notifications;
